/**
 * RISKEE - Workplace Health & Safety Platform
 * Main Application Shell
 * 
 * Routes:
 * - / : Public landing page (unauthenticated)
 * - /demo : Read-only product demo (unauthenticated)
 * - /checkout/success : Post-purchase success page
 * - /app/* : Authenticated application
 */
import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { FeaturesProvider, useFeatures } from './hooks/useFeatures';
import { useReportsDashboard } from './hooks/useReportsDashboard';
import { Button } from './components/ui/button';
import { Toaster } from './components/ui/sonner';
import SyncStatus from './components/SyncStatus';
import { UpgradeBanner, UpgradeModal, useUpgradeModal } from './components/UpgradeBanner';
import './App.css';
import './styles/whs-modules.css';
import './styles/quick-actions.css';
import QuickActionsWidget from './components/QuickActionsWidget';

// Page imports
import LandingPage from './pages/LandingPage';
import DemoPage from './pages/DemoPage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';
import LoginPage from './pages/LoginPage';
import AuditLogPage from './pages/AuditLogPage';
import NewReportPage from './pages/NewReportPage';
import RisksListPage from './pages/RisksListPage';
import RiskDetailPage from './pages/RiskDetailPage';
import BillingPage from './pages/BillingPage';
import APIKeysPage from './pages/APIKeysPage';
import DownloadsPage from './pages/DownloadsPage';

// WHS Module Pages
import DangerousGoodsPage from './pages/DangerousGoodsPage';
import FireSafetyPage from './pages/FireSafetyPage';
import EquipmentPage from './pages/EquipmentPage';
import MajorIncidentsPage from './pages/MajorIncidentsPage';
import WelfarePage from './pages/WelfarePage';

// Shared components
import { Spinner, Card, StatusBadge, SafetyIcon, EmptyState, Select, ActionStatusBadge } from './components/shared';
import api, { formatDate, formatDateTime } from './lib/api';
import { 
  STATUS_CONFIG, SEVERITY_CONFIG, REPORT_TYPES, ACTION_STATUS_CONFIG,
  DG_CLASSES, FIRE_SAFETY_TYPES, EQUIPMENT_CATEGORIES, INCIDENT_CATEGORIES 
} from './lib/constants';

// Lucide icons for dashboard
import { FileText, AlertTriangle, Clock, CheckCircle } from 'lucide-react';

// Dashboard breakdown components (inline for now - can extract later)
import { StatusBreakdownCard, TypeBreakdownCard, SiteBreakdownCard, RecentReportsCard } from './components/dashboard';

// ============== NAV ICONS ==============
const NavIcons = {
  dashboard: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>,
  reports: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>,
  actions: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>,
  inspections: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>,
  dg: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/></svg>,
  fireSafety: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/></svg>,
  incidents: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
  equipment: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>,
  welfare: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>,
  supervisor: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/></svg>,
  admin: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
  auditLog: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M16 13H8"/><path d="M16 17H8"/></svg>,
};

// Navigation configuration
// Note: features array contains feature names required for the nav item
const NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: 'dashboard' },
  { id: 'risks', label: 'Reports', icon: 'reports' },
  { id: 'actions', label: 'Actions', icon: 'actions' },
  { id: 'inspections', label: 'Inspections', icon: 'inspections', features: ['inspections'] },
  { id: 'dg', label: 'Dangerous Goods', icon: 'dg', roles: ['SUPERVISOR', 'RISK_TEAM', 'ORG_ADMIN'] },
  { id: 'fire-safety', label: 'Fire Safety', icon: 'fireSafety', roles: ['SUPERVISOR', 'RISK_TEAM', 'ORG_ADMIN'] },
  { id: 'incidents', label: 'Major Incidents', icon: 'incidents', roles: ['SUPERVISOR', 'RISK_TEAM', 'ORG_ADMIN'] },
  { id: 'equipment', label: 'Equipment', icon: 'equipment', roles: ['SUPERVISOR', 'RISK_TEAM', 'ORG_ADMIN'] },
  { id: 'welfare', label: 'Welfare', icon: 'welfare', roles: ['SUPERVISOR', 'RISK_TEAM', 'ORG_ADMIN'] },
  { id: 'supervisor', label: 'Supervisor', icon: 'supervisor', roles: ['SUPERVISOR', 'RISK_TEAM', 'ORG_ADMIN', 'EXECUTIVE'] },
  { id: 'audit-logs', label: 'Audit Log', icon: 'auditLog', roles: ['SUPERVISOR', 'RISK_TEAM', 'ORG_ADMIN'], features: ['audit_logs'] },
  { id: 'admin', label: 'Admin', icon: 'admin', roles: ['ORG_ADMIN'], features: ['multi_user'] },
];

const ROLES = [
  { id: 'REPORTER', label: 'Staff / Reporter' },
  { id: 'SUPERVISOR', label: 'Supervisor' },
  { id: 'RISK_TEAM', label: 'Risk Team' },
  { id: 'ORG_ADMIN', label: 'Org Admin' },
];

// ============== HEADER ==============
const Header = ({ currentRole, user, onLogout, currentPage, setCurrentPage, onShowUpgrade }) => {
  const { has: hasFeature, tier } = useFeatures();
  
  // Filter nav items by role, but keep feature-gated items visible (locked) for upsell
  const navItems = NAV_ITEMS.map(item => {
    // Check role requirement
    const roleAllowed = !item.roles || item.roles.includes(currentRole);
    if (!roleAllowed) return null;
    
    // Check feature requirement
    const featureLocked = item.features && item.features.length > 0 && 
      !item.features.every(f => hasFeature(f));
    
    return { ...item, locked: featureLocked };
  }).filter(Boolean);
  
  const currentRoleConfig = ROLES.find(r => r.id === currentRole);
  const [isOffline, setIsOffline] = React.useState(!navigator.onLine);
  
  React.useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleNavClick = (item) => {
    if (item.locked) {
      // Show upgrade modal for locked features
      const featureName = item.features?.[0];
      onShowUpgrade?.(featureName, item.label);
    } else {
      setCurrentPage(item.id);
    }
  };

  const handleNewReport = () => {
    if (isOffline) {
      // Import toast dynamically to avoid circular deps
      import('sonner').then(({ toast }) => {
        toast.warning('Create report unavailable offline (v1)', {
          description: 'Reconnect to continue.',
          duration: 4000,
        });
      });
      return;
    }
    setCurrentPage('new-report');
  };

  return (
    <header className="header" data-testid="header">
      <div className="header-content">
        <nav className="header-nav" data-testid="main-nav">
          {navItems.map(item => (
            <button
              key={item.id}
              className={`nav-item ${currentPage === item.id ? 'active' : ''} ${item.locked ? 'nav-locked' : ''}`}
              onClick={() => handleNavClick(item)}
              data-testid={`nav-${item.id}`}
              title={item.locked ? `Requires PRO - Click to upgrade` : item.label}
            >
              <span className="nav-icon-svg">{NavIcons[item.icon]}</span>
              <span className="nav-label">{item.label}</span>
              {item.locked && <span className="nav-lock-badge">PRO</span>}
            </button>
          ))}
        </nav>

        <div className="header-actions">
          <div className="user-context" data-testid="user-context">
            <div className="user-info">
              <span className="user-name">{user?.display_name || 'User'}</span>
              <span className="user-role">{currentRoleConfig?.label || currentRole}</span>
            </div>
            <button className="btn btn-ghost btn-sm logout-btn" onClick={onLogout} data-testid="logout-btn" title="Sign out">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
            </button>
          </div>
          <Button 
            variant="primary" 
            size="sm" 
            onClick={handleNewReport} 
            data-testid="new-report-btn"
            className={isOffline ? 'offline-disabled' : ''}
            title={isOffline ? 'Unavailable offline' : 'Create new report'}
          >
            + New Report
          </Button>
        </div>
      </div>
    </header>
  );
};

// ============== DASHBOARD PAGE (Inline - uses hooks) ==============
const DashboardPage = ({ setCurrentPage, setSelectedRisk }) => {
  const { loading, error, summary, byStatus, byType, bySite, recentRisks, lastUpdated, scope } = useReportsDashboard();

  if (loading) return <div className="page-loading"><Spinner /></div>;
  if (error) return <EmptyState icon="📊" title="Unable to load dashboard" description={error} />;

  return (
    <div className="page dashboard-page" data-testid="dashboard-page">
      {/* Upgrade banner for BASIC tier users */}
      <UpgradeBanner onUpgrade={() => setCurrentPage('settings/billing')} />
      
      <div className="page-header">
        <div>
          <h1>Dashboard</h1>
          <p>Overview of risk and safety management</p>
        </div>
        <div className="data-provenance" data-testid="data-provenance">
          <span className="provenance-item">
            <span className="provenance-label">Last updated:</span>
            <span className="provenance-value">{lastUpdated}</span>
          </span>
          <span className="provenance-divider">•</span>
          <span className="provenance-item">
            <span className="provenance-label">Scope:</span>
            <span className="provenance-value">{scope}</span>
          </span>
        </div>
      </div>

      {/* Quick Actions Widget - Overdue items across all modules */}
      <QuickActionsWidget setCurrentPage={setCurrentPage} />

      <div className="dashboard-summary" data-testid="dashboard-summary">
        <Card className="summary-card">
          <div className="summary-icon" style={{ backgroundColor: '#eff6ff' }}>
            <FileText size={20} strokeWidth={1.5} style={{ color: '#3b82f6' }} />
          </div>
          <div className="summary-content">
            <span className="summary-value">{summary.open_risks ?? 0}</span>
            <span className="summary-label">Open Reports</span>
          </div>
        </Card>
        <Card className="summary-card">
          <div className="summary-icon" style={{ backgroundColor: '#fef2f2' }}>
            <AlertTriangle size={20} strokeWidth={1.5} style={{ color: '#ef4444' }} />
          </div>
          <div className="summary-content">
            <span className="summary-value" style={{ color: '#ef4444' }}>{summary.critical_high ?? 0}</span>
            <span className="summary-label">Critical/High</span>
          </div>
        </Card>
        <Card className="summary-card">
          <div className="summary-icon" style={{ backgroundColor: '#fffbeb' }}>
            <Clock size={20} strokeWidth={1.5} style={{ color: '#f59e0b' }} />
          </div>
          <div className="summary-content">
            <span className="summary-value" style={{ color: '#f59e0b' }}>{summary.overdue_actions ?? 0}</span>
            <span className="summary-label">Overdue Actions</span>
          </div>
        </Card>
        <Card className="summary-card">
          <div className="summary-icon" style={{ backgroundColor: '#ecfdf5' }}>
            <CheckCircle size={20} strokeWidth={1.5} style={{ color: '#10b981' }} />
          </div>
          <div className="summary-content">
            <span className="summary-value" style={{ color: '#10b981' }}>{summary.closed_this_week ?? 0}</span>
            <span className="summary-label">Closed This Week</span>
          </div>
        </Card>
      </div>

      <div className="dashboard-grid">
        <StatusBreakdownCard byStatus={byStatus} totalRisks={summary.total_risks ?? 0} onViewAll={() => setCurrentPage('risks')} />
        <TypeBreakdownCard byType={byType} onViewAll={() => setCurrentPage('risks')} />
        <SiteBreakdownCard bySite={bySite} onViewAll={() => setCurrentPage('risks')} />
        <RecentReportsCard recentRisks={recentRisks} onRiskClick={(riskId) => { setSelectedRisk(riskId); setCurrentPage('risk-detail'); }} onViewAllClick={() => setCurrentPage('risks')} />
      </div>
    </div>
  );
};

// ============== PLACEHOLDER PAGES (To be extracted) ==============
// These remain inline temporarily - extract to /pages/ as needed

const ActionsPageInline = ({ currentRole }) => {
  const [actions, setActions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [filter, setFilter] = React.useState('');

  React.useEffect(() => { loadData(); }, [filter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = filter ? `?status=${filter}` : '';
      const res = await api.get(`/actions${params}`);
      setActions(res.items);
    } catch (err) { console.error('Failed to load actions:', err); }
    finally { setLoading(false); }
  };

  const overdueCount = actions.filter(a => !['VERIFIED', 'DONE', 'CANCELLED'].includes(a.status) && new Date(a.due_date) < new Date()).length;

  return (
    <div className="page actions-page" data-testid="actions-page">
      <div className="page-header">
        <div><h1>Actions</h1><p>Track and manage corrective actions</p></div>
        {overdueCount > 0 && <div className="overdue-alert">⏰ {overdueCount} overdue action{overdueCount > 1 ? 's' : ''}</div>}
      </div>
      <Card className="filters-card">
        <Select label="Status" value={filter} onChange={(e) => setFilter(e.target.value)}
          options={[{ value: '', label: 'All' }, ...Object.entries(ACTION_STATUS_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))]} />
      </Card>
      {loading ? <div className="page-loading"><Spinner /></div> : actions.length === 0 ? 
        <EmptyState icon="✅" title="No actions" description="No actions match the current filter" /> :
        <div className="actions-grid">{actions.map(a => (
          <Card key={a.id} className={`action-card ${!['VERIFIED','DONE','CANCELLED'].includes(a.status) && new Date(a.due_date) < new Date() ? 'overdue' : ''}`}>
            <div className="action-header"><ActionStatusBadge status={a.status} /><span className={`action-priority priority-${a.priority}`}>{a.priority}</span></div>
            <h4>{a.title}</h4>
            <div className="action-meta"><span>Assigned: {a.assigned_to_name}</span><span>Due: {formatDate(a.due_date)}</span></div>
          </Card>
        ))}</div>}
    </div>
  );
};

const InspectionsPageInline = ({ currentRole }) => (
  <div className="page" data-testid="inspections-page">
    <div className="page-header"><h1>Inspections</h1><p>Manage workplace inspections and audits</p></div>
    <EmptyState icon="📋" title="Inspections Module" description="Coming soon - this module is being migrated" />
  </div>
);

const AdminPageInline = () => (
  <div className="page" data-testid="admin-page">
    <div className="page-header"><h1>Admin</h1><p>System administration and user management</p></div>
    <EmptyState icon="⚙️" title="Admin Module" description="Coming soon - this module is being migrated" />
  </div>
);

const SupervisorDashboardPageInline = ({ currentRole }) => {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    api.get('/supervisor/dashboard').then(setData).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="page-loading"><Spinner /></div>;
  if (!data) return <div className="page-error">Unable to load dashboard</div>;

  return (
    <div className="page supervisor-page" data-testid="supervisor-page">
      <div className="page-header"><h1>Supervisor Dashboard</h1><p>Operational KPIs and compliance overview</p></div>
      <div className="kpi-grid">
        <Card className="kpi-card"><div className="kpi-value" style={{ color: data.open_corrective_actions > 0 ? '#f59e0b' : '#10b981' }}>{data.open_corrective_actions}</div><div className="kpi-label">Open Actions</div></Card>
        <Card className="kpi-card"><div className="kpi-value" style={{ color: data.overdue_actions > 0 ? '#ef4444' : '#10b981' }}>{data.overdue_actions}</div><div className="kpi-label">Overdue Actions</div></Card>
        <Card className="kpi-card"><div className="kpi-value" style={{ color: data.critical_risks > 0 ? '#ef4444' : '#10b981' }}>{data.critical_risks}</div><div className="kpi-label">Critical Risks</div></Card>
        <Card className="kpi-card"><div className="kpi-value">{data.incidents_this_month}</div><div className="kpi-label">Incidents This Month</div></Card>
      </div>
      <div className="compliance-section">
        <h2>Compliance Scores</h2>
        <div className="compliance-grid">
          <Card className="compliance-card">
            <div className="compliance-header"><img src="/icons/extinguisher.png" alt="" style={{ width: 32, height: 32 }} /><span>Fire Safety</span></div>
            <div className="compliance-score" style={{ color: data.fire_safety_compliance >= 80 ? '#10b981' : '#f59e0b' }}>{data.fire_safety_compliance}%</div>
            <div className="compliance-detail">{data.fire_equipment_due_service} of {data.fire_equipment_total} items due</div>
          </Card>
          <Card className="compliance-card">
            <div className="compliance-header"><img src="/icons/flammable.png" alt="" style={{ width: 32, height: 32 }} /><span>Dangerous Goods</span></div>
            <div className="compliance-score" style={{ color: data.dg_compliance >= 80 ? '#10b981' : '#f59e0b' }}>{data.dg_compliance}%</div>
            <div className="compliance-detail">{data.sds_expiring_soon} SDS expiring soon</div>
          </Card>
        </div>
      </div>
    </div>
  );
};

// ============== SIMPLE ROUTER ==============
// Handles: / (landing), /checkout/success, /app/* (authenticated)
function useSimpleRouter() {
  const [route, setRoute] = useState(window.location.pathname);

  useEffect(() => {
    const handlePopState = () => setRoute(window.location.pathname);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigate = (path) => {
    window.history.pushState({}, '', path);
    setRoute(path);
  };

  return { route, navigate };
}

// ============== MAIN APP ==============
function AppContent() {
  const { user, isAuthenticated, loading, logout, primaryRole } = useAuth();
  const { route, navigate } = useSimpleRouter();
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [selectedRisk, setSelectedRisk] = useState(null);
  
  // Upgrade modal state
  const { isOpen: upgradeModalOpen, featureName, featureLabel, showUpgradeModal, closeModal } = useUpgradeModal();

  const currentRole = primaryRole;

  // Handle "Download RISKEE" / Get Started - go to login/checkout
  const handleGetStarted = React.useCallback(() => {
    navigate('/app');
  }, [navigate]);

  // Handle "Try Demo" - go to demo page
  const handleTryDemo = React.useCallback(() => {
    navigate('/demo');
  }, [navigate]);

  // Handle exit demo - go back to landing
  const handleExitDemo = React.useCallback(() => {
    navigate('/');
  }, [navigate]);

  // Handle purchase from demo - go to app/checkout
  const handlePurchaseFromDemo = React.useCallback(() => {
    navigate('/app');
  }, [navigate]);

  // Handle "Open RISKEE App" from success page
  const handleOpenApp = React.useCallback(() => {
    navigate('/app');
  }, [navigate]);

  // Handle "Open Downloads" from success page
  const handleOpenDownloads = React.useCallback(() => {
    setCurrentPage('settings/downloads');
    navigate('/app');
  }, [navigate]);

  // Handle successful login - trigger queue flush
  const handleLoginSuccess = React.useCallback(() => {
    setCurrentPage('dashboard');
    
    // Clear reauth flag and trigger queue flush after login
    localStorage.removeItem('riskee_reauth_required');
    
    // Give SW time to get the new token, then flush
    setTimeout(async () => {
      try {
        const registration = await navigator.serviceWorker?.ready;
        if (registration?.active) {
          console.log('[App] Login success - triggering queue flush');
          registration.active.postMessage({ type: 'SYNC_NOW' });
        }
      } catch (e) {
        console.error('[App] Failed to trigger post-login sync:', e);
      }
    }, 500);
  }, []);

  // Handle reauth required from SyncStatus - navigate to login
  const handleLoginRequired = React.useCallback(() => {
    // Force logout to show login page
    logout();
  }, [logout]);

  // Handle navigate to billing (from UpgradeBanner/UpgradeModal)
  const handleNavigateBilling = React.useCallback(() => {
    setCurrentPage('settings/billing');
  }, []);

  // Listen for billing navigation events
  useEffect(() => {
    const handler = () => handleNavigateBilling();
    window.addEventListener('navigate-billing', handler);
    return () => window.removeEventListener('navigate-billing', handler);
  }, [handleNavigateBilling]);

  // Handle checkout return with session_id
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const status = params.get('status');

    if (sessionId && status === 'success') {
      // Navigate to success page
      navigate('/checkout/success');
      // Clean URL params
      window.history.replaceState({}, '', '/checkout/success');
    }
  }, [navigate]);

  // PUBLIC ROUTES (no auth required)
  
  // Landing page - root route
  if (route === '/' || route === '') {
    return <LandingPage onGetStarted={handleGetStarted} onTryDemo={handleTryDemo} />;
  }

  // Demo page - read-only product preview
  if (route === '/demo') {
    return <DemoPage onPurchase={handlePurchaseFromDemo} onExit={handleExitDemo} />;
  }

  // Checkout success page
  if (route === '/checkout/success') {
    return <CheckoutSuccessPage onOpenApp={handleOpenApp} onOpenDownloads={handleOpenDownloads} />;
  }

  // PROTECTED ROUTES (auth required)
  
  if (loading) {
    return <div className="app-loading"><Spinner /><p>Loading...</p></div>;
  }

  if (!isAuthenticated) {
    return <LoginPage onSuccess={handleLoginSuccess} />;
  }

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <DashboardPage setCurrentPage={setCurrentPage} setSelectedRisk={setSelectedRisk} />;
      case 'risks':
        return <RisksListPage setCurrentPage={setCurrentPage} setSelectedRisk={setSelectedRisk} currentRole={currentRole} />;
      case 'new-report':
        return <NewReportPage setCurrentPage={setCurrentPage} currentRole={currentRole} />;
      case 'risk-detail':
        return <RiskDetailPage riskId={selectedRisk} setCurrentPage={setCurrentPage} currentRole={currentRole} />;
      case 'actions':
        return <ActionsPageInline currentRole={currentRole} />;
      case 'inspections':
        return <InspectionsPageInline currentRole={currentRole} />;
      case 'welfare':
        return <WelfarePage />;
      case 'dg':
        return <DangerousGoodsPage />;
      case 'fire-safety':
        return <FireSafetyPage />;
      case 'incidents':
        return <MajorIncidentsPage />;
      case 'equipment':
        return <EquipmentPage />;
      case 'supervisor':
        return <SupervisorDashboardPageInline currentRole={currentRole} />;
      case 'audit-logs':
        return <AuditLogPage />;
      case 'admin':
        return <AdminPageInline />;
      case 'settings/billing':
        return <BillingPage onBack={() => setCurrentPage('dashboard')} onNavigate={setCurrentPage} />;
      case 'settings/api-keys':
        return <APIKeysPage onBack={() => setCurrentPage('settings/billing')} />;
      case 'settings/downloads':
        return <DownloadsPage onBack={() => setCurrentPage('dashboard')} />;
      default:
        return <DashboardPage setCurrentPage={setCurrentPage} setSelectedRisk={setSelectedRisk} />;
    }
  };

  return (
    <div className="app" data-testid="app">
      <Header 
        currentRole={currentRole} 
        user={user} 
        onLogout={logout} 
        currentPage={currentPage} 
        setCurrentPage={setCurrentPage}
        onShowUpgrade={showUpgradeModal}
      />
      <main className="main-content">{renderPage()}</main>
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-brand"><img src="/riskee_logo.png" alt="" className="footer-logo" /><span>RISKEE</span></div>
          <span className="footer-text">A Standards Protocol Platform</span>
        </div>
      </footer>
      <SyncStatus onLoginRequired={handleLoginRequired} />
      <Toaster position="top-right" richColors />
      <UpgradeModal 
        isOpen={upgradeModalOpen} 
        onClose={closeModal} 
        featureName={featureName} 
        featureLabel={featureLabel}
        onUpgrade={handleNavigateBilling}
      />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <FeaturesProvider>
        <AppContent />
      </FeaturesProvider>
    </AuthProvider>
  );
}

export default App;
